import { useCallback } from 'react'
import { getYaNative } from '@yanolja-rnd/ya-native'
import { isNative } from '~/app/utils/typeguards'
import conf from '~/app/conf'
import qs from 'qs'

/** 로그인 함수의 옵션 인터페이스 */
export interface LoginOptions {
  /** 로그인 페이지로 이동할 때 현재 페이지를 히스토리에서 교체할지 여부 (기본값: true) */
  isReplaceHistory?: boolean
  /** 리다이렉트 URL에서 hash를 제거할지 여부 (기본값: true) */
  removeHash?: boolean
}

/**
 * 현재 브라우저의 URL을 가져오는 헬퍼 함수
 *
 * @param removeHash - hash 부분을 제거할지 여부 (기본값: true)
 * @returns 현재 URL 문자열 (hash 제거 여부에 따라)
 */
const getCurrentUrl = (removeHash = true): string => {
  const { href, hash } = window.location
  return removeHash && hash ? href.replace(hash, '') : href
}

/**
 * 로그인 페이지 URL을 생성하는 헬퍼 함수
 *
 * @param redirectUrl - 로그인 완료 후 리다이렉트될 URL
 * @returns conf.account.url에 redirect 파라미터가 포함된 완전한 로그인 URL
 */
const createLoginUrl = (redirectUrl: string): string => {
  const queryString = qs.stringify({ redirectUrl })
  return `${conf.account.url}?${queryString}`
}

/**
 * 로그인 기능을 제공하는 커스텀 훅
 *
 * 네이티브 앱과 웹 환경을 모두 지원하며, 환경에 따라 적절한 로그인 방식을 선택합니다.
 * - 네이티브 앱: ya-native의 login() 함수 호출
 * - 웹 환경: conf.account.url로 리다이렉트 (현재 URL을 redirect 파라미터로 전달)
 */
export const useLogin = () => {
  /**
   * 로그인을 실행하는 함수
   *
   * @param options - 로그인 옵션 객체
   * @param options.isReplaceHistory - 현재 페이지를 히스토리에서 교체할지 여부 (기본값: true)
   * @param options.removeHash - 리다이렉트 URL에서 hash를 제거할지 여부 (기본값: true)
   */
  const onLogin = useCallback(
    ({ isReplaceHistory = true, removeHash = true }: LoginOptions = {}) => {
      const yaNative = getYaNative()

      if (isNative(yaNative)) {
        yaNative.login()
      } else {
        const currentUrl = getCurrentUrl(removeHash)
        const loginUrl = createLoginUrl(currentUrl)

        // 히스토리 처리 방식에 따라 분기
        if (isReplaceHistory) {
          window.location.replace(loginUrl)
        } else {
          window.location.href = loginUrl
        }
      }
    },
    []
  )

  return { onLogin }
}

/**
 * 컴포넌트 외부에서 사용할 수 있는 standalone 로그인 함수
 *
 * @param options - 로그인 옵션 객체
 * @param options.isReplaceHistory - 현재 페이지를 히스토리에서 교체할지 여부 (기본값: true)
 * @param options.removeHash - 리다이렉트 URL에서 hash를 제거할지 여부 (기본값: true)
 */
export const login = ({ isReplaceHistory = true, removeHash = true }: LoginOptions = {}) => {
  const yaNative = getYaNative()

  if (isNative(yaNative)) {
    yaNative.login()
  } else {
    const currentUrl = getCurrentUrl(removeHash)
    const loginUrl = createLoginUrl(currentUrl)

    if (isReplaceHistory) {
      window.location.replace(loginUrl)
    } else {
      window.location.href = loginUrl
    }
  }
}
